import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid2";
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchrecommendedInstruments, fetchOptionChainConfigs } from "../services/api";

const RecommendedInstruments = () => {
    const [suggestedData, setSuggestedData] = useState(null);
    const [instrumentCombinations, setInstrumentCombinations] = useState([]);
    const [selectedCombination, setSelectedCombination] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingInstruments, setIsLoadingInstruments] = useState(false);
    const [error, setError] = useState(null);

    // Fetch instrument combinations from API
    const fetchInstrumentCombinations = async () => {
        setIsLoadingInstruments(true);
        setError(null);
        try {
            const response = await fetchOptionChainConfigs();
            if (response.status === "success" && response.data?.configs) {
                // Transform API response to component format
                const combinations = response.data.configs.map((config) => ({
                    label: `${config.underlying_symbol} (${config.expiry})`,
                    index_name: config.underlying_symbol,
                    expiry_date: config.expiry,
                }));
                setInstrumentCombinations(combinations);
                
                // Set first combination as selected if available
                if (combinations.length > 0 && !selectedCombination) {
                    setSelectedCombination(combinations[0]);
                }
            }
        } catch (err) {
            setError(`Failed to load instruments: ${err.message}`);
            console.error('Error fetching instrument combinations:', err);
        } finally {
            setIsLoadingInstruments(false);
        }
    };

    const fetchData = async (allowCachedData = true) => {
        if (!selectedCombination) return;
        
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetchrecommendedInstruments(
                selectedCombination.index_name,
                selectedCombination.expiry_date,
                allowCachedData
            );
            setSuggestedData(response.data);
        } catch (err) {
            setError(`Failed to load data: ${err.message}`);
            console.error('Error fetching suggested instruments:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch instrument combinations on component mount
    useEffect(() => {
        fetchInstrumentCombinations();
    }, []);

    // Fetch data when selected combination changes
    useEffect(() => {
        if (selectedCombination) {
            fetchData();
        }
    }, [selectedCombination]);

    const handleRefresh = () => {
        fetchData();
    };

    const handleForceRefresh = () => {
        fetchData(false);
    };

    const handleCombinationChange = (event) => {
        const combination = instrumentCombinations.find(c => c.label === event.target.value);
        if (combination) {
            setSelectedCombination(combination);
        }
    };

    const formatNumber = (num, precision = 2) => {
        if (num === null || num === undefined) return '-';
        if (typeof num === 'number') {
            return num.toLocaleString('en-IN', {
                minimumFractionDigits: precision,
                maximumFractionDigits: precision
            });
        }
        return num;
    };

    const getScoreColor = (score) => {
        if (score >= 0.8) return 'success';
        if (score >= 0.6) return 'error';
        return 'error';
    };

    const getStrikeLevelColor = (level) => {
        if (level.includes('ITM')) return 'success';
        if (level.includes('OTM')) return 'error';
        return 'default';
    };

    const renderStrategySection = (strategyName, strategyData) => {
        if (!strategyData) return null;

        return (
            <Accordion key={strategyName} defaultExpanded={strategyName === 'scalping'}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                        {strategyName.replace('_', ' ')}
                    </Typography>
                    {/* <Box sx={{ ml: 2 }}>
                        <Chip 
                            label={`${strategyData.summary?.total_options_analyzed || 0} Options Analyzed`}
                            size="small"
                            variant="outlined"
                        />
                    </Box> */}
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        {/* Calls Section */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card elevation={1}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom color="success.main">
                                        CALL Options
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Rank</TableCell>
                                                    <TableCell>Strike</TableCell>
                                                    <TableCell>Level</TableCell>
                                                    <TableCell>Score</TableCell>
                                                    <TableCell>LTP</TableCell>
                                                    <TableCell>Delta</TableCell>
                                                    <TableCell>Gamma</TableCell>
                                                    <TableCell>Theta</TableCell>
                                                    <TableCell>Vega</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {strategyData.calls?.slice(0, 5).map((call, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{call.rank}</TableCell>
                                                        <TableCell>{formatNumber(call.strike, 0)}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={call.strike_level}
                                                                size="small"
                                                                color={getStrikeLevelColor(call.strike_level)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={formatNumber(call.score)}
                                                                size="small"
                                                                color={getScoreColor(call.score)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{formatNumber(call.last_price)}</TableCell>
                                                        <TableCell>{formatNumber(call.greeks?.delta)}</TableCell>
                                                        <TableCell>{formatNumber(call.greeks?.gamma)}</TableCell>
                                                        <TableCell>{formatNumber(call.greeks?.theta)}</TableCell>
                                                        <TableCell>{formatNumber(call.greeks?.vega)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Puts Section */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card elevation={1}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom color="error.main">
                                        PUT Options
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Rank</TableCell>
                                                    <TableCell>Strike</TableCell>
                                                    <TableCell>Level</TableCell>
                                                    <TableCell>Score</TableCell>
                                                    <TableCell>LTP</TableCell>
                                                    <TableCell>Delta</TableCell>
                                                    <TableCell>Gamma</TableCell>
                                                    <TableCell>Theta</TableCell>
                                                    <TableCell>Vega</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {strategyData.puts?.slice(0, 5).map((put, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{put.rank}</TableCell>
                                                        <TableCell>{formatNumber(put.strike, 0)}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={put.strike_level}
                                                                size="small"
                                                                color={getStrikeLevelColor(put.strike_level)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={formatNumber(put.score)}
                                                                size="small"
                                                                color={getScoreColor(put.score)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{formatNumber(put.last_price)}</TableCell>
                                                        <TableCell>{formatNumber(put.greeks?.delta)}</TableCell>
                                                        <TableCell>{formatNumber(put.greeks?.gamma)}</TableCell>
                                                        <TableCell>{formatNumber(put.greeks?.theta)}</TableCell>
                                                        <TableCell>{formatNumber(put.greeks?.vega)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        );
    };

    return (
        <Box>
            {/* Header Section */}
            <Box mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    {/* <Box>
                        <Typography variant="h4" fontWeight="bold" gutterBottom>
                            Suggested Instruments
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            AI-powered instrument recommendations for various trading strategies
                        </Typography>
                    </Box> */}

                    <Box display="flex" gap={3} alignItems="center" flexWrap="wrap">
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Instrument & Expiry</InputLabel>
                            <Select
                                value={selectedCombination?.label || ''}
                                label="Instrument & Expiry"
                                onChange={handleCombinationChange}
                                disabled={isLoadingInstruments || instrumentCombinations.length === 0}
                            >
                                {instrumentCombinations.map((combination) => (
                                    <MenuItem key={combination.label} value={combination.label}>
                                        {combination.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {isLoadingInstruments && (
                            <CircularProgress size={20} />
                        )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={handleRefresh} disabled={isLoading}>
                                {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            onClick={handleForceRefresh}
                            disabled={isLoading}
                            startIcon={<RefreshIcon />}
                        >
                            Force Refresh
                        </Button>
                    </Box>
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Instrument Selection */}
                {/* <Card elevation={1} sx={{ mb: 3 }}>
                    <CardContent>
                        <Box display="flex" gap={3} alignItems="center" flexWrap="wrap">
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Instrument & Expiry</InputLabel>
                                <Select
                                    value={selectedCombination.label}
                                    label="Instrument & Expiry"
                                    onChange={handleCombinationChange}
                                >
                                    {INSTRUMENT_COMBINATIONS.map((combination) => (
                                        <MenuItem key={combination.label} value={combination.label}>
                                            {combination.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {suggestedData?.analysis?.timestamp && (
                                <Typography variant="caption" color="text.secondary">
                                    Last updated: {new Date(suggestedData.analysis.timestamp).toLocaleString('en-IN', {
                                        timeZone: 'Asia/Kolkata',
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        hour12: false
                                    })}
                                </Typography>
                            )}
                        </Box>
                    </CardContent>
                </Card> */}
            </Box>

            {/* Loading State */}
            {isLoading && (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            )}

            {/* Data Display */}
            {suggestedData && !isLoading && (
                <Box>
                    {/* Analysis Summary */}
                    <Card elevation={2} sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Analysis Summary
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Box textAlign="center">
                                        <Typography variant="h4" color="primary.main">
                                            {suggestedData.analysis?.instrument || '-'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Instrument
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Box textAlign="center">
                                        <Typography variant="h4" color="primary.main">
                                            {formatNumber(suggestedData.analysis?.spot_price)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Spot Price
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Box textAlign="center">
                                        <Typography variant="h4" color="primary.main">
                                            {suggestedData.analysis?.expiry_date || '-'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Expiry Date
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Box textAlign="center">
                                        <Typography variant="h4" color="primary.main">
                                            {Object.keys(suggestedData.analysis?.strategies || {}).length}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Strategies Analyzed
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Strategy Sections */}
                    {suggestedData.analysis?.strategies && (
                        <Box>
                            {Object.entries(suggestedData.analysis.strategies).map(([strategyName, strategyData]) =>
                                renderStrategySection(strategyName, strategyData)
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {/* No Data State */}
            {!suggestedData && !isLoading && !error && (
                <Card elevation={2}>
                    <CardContent>
                        <Box textAlign="center" py={4}>
                            <Typography variant="h6" color="text.secondary">
                                No data available. Please select an instrument combination and refresh.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default RecommendedInstruments;
